import React, { useState } from 'react';

// ============================================================
// Compose Email modal
// ============================================================
// Reusable for two flows:
//   • New email — pass `to` + (optionally) `defaultSubject`
//   • Reply to existing — pass `replyToEmailId` (and a hint at
//     the original subject for header display)
//
// V1 is plain text. Rich-text + attachments come later.

interface ComposeProps {
  to?: string;
  toName?: string;
  defaultSubject?: string;
  // If set, sends via the /reply endpoint to preserve thread.
  // The original message is auto-quoted by Microsoft Graph.
  replyToEmailId?: number;
  replyOriginalSubject?: string;
  replyOriginalPreview?: string;
  onClose: () => void;
  onSent?: () => void;
}

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

export const ComposeEmailModal: React.FC<ComposeProps> = ({
  to, toName, defaultSubject, replyToEmailId, replyOriginalSubject, replyOriginalPreview,
  onClose, onSent
}) => {
  const isReply = !!replyToEmailId;

  const [toInput, setToInput]       = useState(to || '');
  const [ccInput, setCcInput]       = useState('');
  const [subject, setSubject]       = useState(
    isReply
      ? `Re: ${(replyOriginalSubject || '').replace(/^(re:\s*)+/i, '')}`
      : (defaultSubject || '')
  );
  const [body, setBody]             = useState('');
  const [showCc, setShowCc]         = useState(false);
  const [sending, setSending]       = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const parseEmails = (s: string): string[] =>
    s.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean);

  const send = async () => {
    setError(null);

    if (!body.trim()) { setError('Message body cannot be empty'); return; }
    if (!isReply && parseEmails(toInput).length === 0) {
      setError('Please add at least one "To" recipient'); return;
    }

    setSending(true);
    try {
      let res: Response;
      if (isReply) {
        res = await fetch(`/api/crm/emails/${replyToEmailId}/reply`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ body })
        });
      } else {
        res = await fetch('/api/crm/emails/send', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            to: parseEmails(toInput),
            cc: parseEmails(ccInput),
            subject,
            body
          })
        });
      }
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'MAIL_SEND_MISSING') {
          setError(
            'Mail.Send permission isn\'t granted on your Azure app yet. ' +
            'Go to portal.azure.com → Marketmapr app → API permissions → Add → Microsoft Graph → Delegated → tick "Mail.Send" → Grant admin consent. ' +
            'Then disconnect + reconnect Outlook from the CRM page so the new scope is included.'
          );
          return;
        }
        throw new Error(data.error || 'Failed to send');
      }
      onSent?.();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {isReply ? 'Reply' : 'New email'}
            </h2>
            {isReply && (
              <p className="text-xs text-gray-500 mt-0.5">
                Replying to <span className="font-medium text-gray-700">{toName || to}</span>
                {' — '}thread will stay together in Outlook.
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
        </div>

        <div className="px-6 py-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800">{error}</div>
          )}

          {/* To */}
          {!isReply && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
              <input
                type="text"
                value={toInput}
                onChange={e => setToInput(e.target.value)}
                placeholder="name@example.com, another@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <button
                onClick={() => setShowCc(s => !s)}
                className="text-[10px] text-blue-600 hover:underline mt-1"
              >
                {showCc ? '− Hide Cc' : '+ Add Cc'}
              </button>
            </div>
          )}
          {!isReply && showCc && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Cc</label>
              <input
                type="text"
                value={ccInput}
                onChange={e => setCcInput(e.target.value)}
                placeholder="name@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
          )}

          {/* Subject — read-only display when replying since Graph handles it */}
          {!isReply ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Subject"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
          ) : (
            <div className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded">
              <span className="text-gray-400">Subject:</span> {subject}
            </div>
          )}

          {/* Body */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Message {isReply && <span className="text-gray-400 font-normal">— original will be quoted below your reply automatically</span>}
            </label>
            <textarea
              autoFocus
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={isReply ? 8 : 12}
              placeholder={isReply ? 'Type your reply…' : 'Write your message…'}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono"
            />
          </div>

          {isReply && replyOriginalPreview && (
            <details className="bg-gray-50 rounded p-2">
              <summary className="cursor-pointer text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                Original message
              </summary>
              <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">{replyOriginalPreview}</p>
            </details>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-between items-center sticky bottom-0 bg-white">
          <div className="text-xs text-gray-500">
            Sends from your connected Outlook · saves to your Sent Items
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            <button
              onClick={send}
              disabled={sending || !body.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {sending ? 'Sending…' : isReply ? 'Send reply' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
